/**
 * Offline queue — IndexedDB-backed store for driver state transitions
 * (START / FINISH) captured while the device has no signal, replayed in FIFO
 * order on reconnect. Key continuity is the dedup backbone: the idempotency
 * key held by the screen (`startKeyRef` / `finishKeyRef`) is the SAME key
 * stored in the queue, so a replayed action is recognized by the server's
 * `idempotency_keys` cache instead of double-processing.
 *
 * Replay classification (Unit #13 audit contracts):
 *   - network error        → pending + BREAK (still offline)
 *   - 2xx                  → done
 *   - 401                  → pending + "Sign-in required" error (stop replay)
 *   - 409 / 400 invalid_transition
 *                          → sync-verify via GET /jobs/:id:
 *                            - FINISH: FINISHED/REVIEWED → done; else failed
 *                            - START: IN_PROGRESS/FINISHED/REVIEWED → done;
 *                                    CANCELLED/REVIEWED/CANCELLED → failed w/ "Job can no longer be started (state: X)"
 *   - 400 gate_failed w/ retryAt (E/R cooldown)
 *                          → pending + retryAt (validate finite timestamp; NaN → failed)
 *   - other 4xx            → failed (terminal)
 *   - 5xx                  → pending + STOP (strict FIFO)
 *
 * Architecture: createQueue() factory (testable, two instances over one IDB)
 * + a default singleton that preserves the original call-site surface.
 */

import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { rawRequest, ApiError, type SessionUser } from "./api";

export type QueuedActionStatus =
  | "pending"
  | "syncing"
  | "done"
  | "failed";

export type QueuedActionType = "START" | "FINISH";

export interface QueuedAction {
  id?: number;
  url: string; // e.g. "/api/v1/jobs/:id/start"
  method: "POST";
  body: unknown; // request body (JSON-able)
  idempotencyKey: string; // SAME key held by the screen ref
  jobId: string; // for sync-verify recovery + UI display
  jobNumber: string; // for UI display
  actionType: QueuedActionType;
  status: QueuedActionStatus;
  queuedAt: number;
  claimedAt?: number;
  /** E/R cooldown unlock moment — pending items skip replay until this passes. */
  retryAt?: number;
  syncedAt?: number;
  error?: string;
}

export interface QueueStatus {
  pending: number;
  failed: number;
  done: number;
  unavailable?: boolean;
  /** Details of pending actions (for the "Finish queued" / "Start queued" chip or auth warning). */
  pendingItems: { jobNumber: string; actionType: QueuedActionType; error?: string }[];
  /** Details of failed actions (error text) for the UI banner. */
  failedItems: { jobNumber: string; actionType: QueuedActionType; error: string }[];
}

export class QueueUnavailableError extends Error {
  constructor(message = "Offline queue unavailable") {
    super(message);
    this.name = "QueueUnavailableError";
  }
}

interface OfflineQueueDB extends DBSchema {
  queue: {
    key: number;
    value: QueuedAction;
    indexes: { byStatus: string };
  };
}

const DB_NAME = "eagledispatch-offline";
const DB_VERSION = 1;
const STORE = "queue";
const STALE_CLAIM_MS = 5 * 60 * 1000;

/**
 * Replay-outcome classifier — the decision core, separated from the
 * IndexedDB/fetch plumbing so it can be unit-tested without a browser.
 *
 * Input: the action type, the raw HTTP status, and the parsed error body.
 * Output: the next persisted state + optional retryAt/error.
 *
 *   network error (status 0) → break (still offline)
 *   2xx                      → done
 *   401                      → pending with error "Sign-in required"
 *   409 or 400 invalid_transition on queued START or FINISH
 *                            → verify-start or verify-finish
 *   400 gate_failed w/ retryAt (E/R cooldown)
 *                            → pending + retryAt (NaN → failed)
 *   other 4xx                → failed (terminal)
 *   5xx                      → pending (stop loop)
 */
export type ReplayOutcome =
  | { kind: "done" }
  | { kind: "break" } // network error — still offline, stop the loop
  | { kind: "pending"; retryAt?: number; error?: string } // 5xx, cooldown, or 401 auth-expired
  | { kind: "verify-finish" } // 409/invalid_transition on FINISH — GET the job
  | { kind: "verify-start" } // 409/invalid_transition on START — GET the job
  | { kind: "failed"; error: string };

export function classifyReplayOutcome(
  actionType: QueuedActionType,
  status: number,
  body: { error?: string; reason?: string; retryAt?: string },
): ReplayOutcome {
  // 2xx → done
  if (status >= 200 && status < 300) return { kind: "done" };

  // 401 → auth expired (dead refresh token)
  if (status === 401) {
    return { kind: "pending", error: "Sign-in required" };
  }

  // 409 or 400 invalid_transition → sync-verify
  if (
    status === 409 ||
    (status === 400 && body.error === "invalid_transition")
  ) {
    if (actionType === "FINISH") return { kind: "verify-finish" };
    if (actionType === "START") return { kind: "verify-start" };
  }

  // E/R cooldown 400 with a retryAt → stay pending until it unlocks.
  if (
    status === 400 &&
    body.error === "gate_failed" &&
    typeof body.retryAt === "string" &&
    body.retryAt.length > 0
  ) {
    const retryAtMs = new Date(body.retryAt).getTime();
    if (!Number.isFinite(retryAtMs)) {
      return { kind: "failed", error: "invalid retryAt from server" };
    }
    return { kind: "pending", retryAt: retryAtMs };
  }

  // Other 4xx → terminal failure.
  if (status >= 400 && status < 500) {
    return {
      kind: "failed",
      error: body.reason || `Rejected (${status}): ${body.error ?? "unknown"}`,
    };
  }

  // 5xx → server hiccup, not offline: stay pending.
  if (status >= 500) return { kind: "pending" };

  // Network error (status 0)
  if (status === 0) return { kind: "break" };

  // Unreachable defensive fallback.
  return { kind: "failed", error: `Unexpected status ${status}` };
}

/** Parse an error body → `{ error, reason, retryAt }` for classification. */
async function parseErrorBody(res: Response): Promise<{
  error?: string;
  reason?: string;
  retryAt?: string;
}> {
  try {
    return (await res.json()) as {
      error?: string;
      reason?: string;
      retryAt?: string;
    };
  } catch {
    return {};
  }
}

/**
 * Sync-verify a FINISH that replayed to 409 / invalid_transition:
 * GET the job and check whether it is already FINISHED/REVIEWED.
 */
async function verifyFinishLanded(
  jobId: string,
  request: typeof rawRequest,
): Promise<"landed" | "not-landed" | "unknown"> {
  let res: Response;
  try {
    res = await request(`/api/v1/jobs/${jobId}`);
  } catch {
    return "unknown";
  }
  if (!res.ok) return "unknown";
  try {
    const job = (await res.json()) as { state?: string };
    if (job.state === "FINISHED" || job.state === "REVIEWED") return "landed";
    return "not-landed";
  } catch {
    return "unknown";
  }
}

/**
 * Sync-verify a START that replayed to 409 / invalid_transition: GET the job
 * and check whether the state has already moved past NEW.
 *
 * Returns the actual server state on not-landed so the failed message can
 * distinguish CANCELLED (terminal, can't retry) from NEW (genuinely still
 * pending) — surface "Job can no longer be started (state: X)" instead of
 * the misleading "job is still NEW".
 */
async function verifyStartLanded(
  jobId: string,
  request: typeof rawRequest,
): Promise<{ kind: "landed" } | { kind: "unknown" } | { kind: "not-landed"; state: string }> {
  let res: Response;
  try {
    res = await request(`/api/v1/jobs/${jobId}`);
  } catch {
    return { kind: "unknown" };
  }
  if (!res.ok) return { kind: "unknown" };
  try {
    const job = (await res.json()) as { state?: string };
    if (
      job.state === "IN_PROGRESS" ||
      job.state === "FINISHED" ||
      job.state === "REVIEWED"
    ) {
      return { kind: "landed" };
    }
    return { kind: "not-landed", state: job.state ?? "UNKNOWN" };
  } catch {
    return { kind: "unknown" };
  }
}

/** Options for createQueue(). */
export interface CreateQueueOptions {
  /** Override the IndexedDB database name (test isolation). */
  dbName?: string;
  /** Inject the HTTP primitive (test seam). Defaults to the production rawRequest. */
  request?: (path: string, options?: RequestInit) => Promise<Response>;
}

/** A single offline-queue instance — per-tab/per-test state. */
export interface OfflineQueue {
  /** Testing helper to inject a mock DB promise or simulate open failure. */
  _setDbForTesting(
    override: Promise<IDBPDatabase<OfflineQueueDB> | null> | null,
  ): void;
  /** Subscribe to queue changes. Returns an unsubscribe function. */
  onQueueChange(callback: () => void): () => void;
  /**
   * Reclaim rows held in "syncing" state. By default only reclaims stale
   * claims (claimed > 5 min ago) — this is the safe path used by replay
   * AND by default init. Pass `forceAll: true` to reclaim ALL syncing rows
   * (only the developer tool / "purge all" path should do this; it would
   * steal a fresh claim from a live tab).
   */
  resetSyncing(options?: { forceAll?: boolean }): Promise<void>;
  /**
   * Store an action for later replay. Throws `QueueUnavailableError` if
   * IndexedDB cannot be opened.
   */
  enqueue(
    action: Omit<
      QueuedAction,
      "id" | "status" | "queuedAt" | "syncedAt" | "error" | "claimedAt"
    > & { retryAt?: number },
  ): Promise<void>;
  /**
   * Wrap a state-transition POST with offline awareness. Returns a synthetic
   * `202 { queued: true }` when any network failure occurs so optimistic
   * navigation continues unchanged.
   *
   * FIX 1: navigator.onLine is a liar (captive portals, DNS failures throw
   * TypeError while onLine=true). ANY fetch rejection (TypeError / status-0
   * ApiError) triggers queueing.
   */
  queuedApiFetch(
    path: string,
    body: unknown,
    meta: {
      idempotencyKey: string;
      jobId: string;
      jobNumber: string;
      actionType: QueuedActionType;
      /** Body persisted into the queue (may differ from the live-attempt body).
       * FINISH replays must carry `{ queuedOffline: true }` even though the live
       * attempt sends an empty body. Defaults to `body`. */
      queuedBody?: unknown;
    },
  ): Promise<Response>;
  /**
   * Re-run pending actions in strict FIFO order.
   *
   * Reclaim stale syncing rows (> 5 min old). Claim inside ONE readwrite
   * transaction (cross-tab serialized). Strict FIFO: loop STOPS at the first
   * row that doesn't reach `done`. `failed` rows are TERMINAL (already
   * surfaced in the UI banner) and are SKIPPED so they don't starve later
   * rows.
   */
  replayQueue(): Promise<void>;
  /** Read the current queue status for UI banners. */
  getQueueStatus(): Promise<QueueStatus>;
  /**
   * Wire the global replay triggers: online, visibilitychange→visible, and
   * a 500ms debounce so online+focus don't double-run. `forceReset: true`
   * reclaims ALL syncing rows on boot (developer path); default is
   * stale-only (safe — does not steal fresh claims from other live tabs).
   */
  init(options?: { forceReset?: boolean }): () => void;
}

/**
 * Factory — creates a fresh offline-queue instance with its own `dbPromise`,
 * `replayInProgress` flag, debounce timer, and change listeners. Two
 * instances over one shared IndexedDB database are how the harness proves
 * the cross-tab claim race against the REAL code (not a copy).
 */
export function createQueue(options: CreateQueueOptions = {}): OfflineQueue {
  const dbName = options.dbName ?? DB_NAME;
  const request = options.request ?? rawRequest;

  let dbPromise: Promise<IDBPDatabase<OfflineQueueDB> | null> | null = null;
  const changeListeners = new Set<() => void>();
  let replayInProgress = false;
  let replayDebounce: ReturnType<typeof setTimeout> | null = null;

  function notifyQueueChange(): void {
    for (const listener of changeListeners) {
      try {
        listener();
      } catch {
        // Listener errors must not break queue operations.
      }
    }
  }

  /** Single shared connection — opened lazily on first use. Gracefully degrades to null on failure. */
  function getDb(): Promise<IDBPDatabase<OfflineQueueDB> | null> {
    if (!dbPromise) {
      dbPromise = openDB<OfflineQueueDB>(dbName, DB_VERSION, {
        upgrade(db) {
          const store = db.createObjectStore(STORE, {
            keyPath: "id",
            autoIncrement: true,
          });
          store.createIndex("byStatus", "status");
        },
      }).catch((err) => {
        console.warn("[offline-queue] IndexedDB open failed; queue unavailable:", err);
        return null;
      });
    }
    return dbPromise;
  }

  function _setDbForTesting(
    override: Promise<IDBPDatabase<OfflineQueueDB> | null> | null,
  ): void {
    dbPromise = override;
  }

  function onQueueChange(callback: () => void): () => void {
    changeListeners.add(callback);
    return () => {
      changeListeners.delete(callback);
    };
  }

  async function resetSyncing(opts: { forceAll?: boolean } = {}): Promise<void> {
    const forceAll = opts.forceAll ?? false;
    const db = await getDb();
    if (!db) return;
    const tx = db.transaction(STORE, "readwrite");
    const syncing = await tx.store.index("byStatus").getAll("syncing");
    const staleCutoff = Date.now() - STALE_CLAIM_MS;
    let resetCount = 0;
    for (const action of syncing) {
      if (forceAll || !action.claimedAt || action.claimedAt < staleCutoff) {
        await tx.store.put({ ...action, status: "pending", claimedAt: undefined });
        resetCount++;
      }
    }
    await tx.done;
    if (resetCount > 0) notifyQueueChange();
  }

  async function enqueue(
    action: Omit<
      QueuedAction,
      "id" | "status" | "queuedAt" | "syncedAt" | "error" | "claimedAt"
    > & { retryAt?: number },
  ): Promise<void> {
    const db = await getDb();
    if (!db) {
      throw new QueueUnavailableError();
    }
    await db.add(STORE, {
      ...action,
      status: "pending",
      queuedAt: Date.now(),
    });
    notifyQueueChange();
  }

  async function queuedApiFetch(
    path: string,
    body: unknown,
    meta: {
      idempotencyKey: string;
      jobId: string;
      jobNumber: string;
      actionType: QueuedActionType;
      queuedBody?: unknown;
    },
  ): Promise<Response> {
    const attempt = async (): Promise<Response> => {
      const res = await request(path, {
        method: "POST",
        headers: { "Idempotency-Key": meta.idempotencyKey },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let parsed: Record<string, unknown> = {};
        try {
          parsed = (await res.json()) as Record<string, unknown>;
        } catch {
          // Non-JSON error body — keep parsed empty.
        }
        throw new ApiError(res.status, parsed ?? {});
      }
      return res;
    };

    try {
      return await attempt();
    } catch (e) {
      const isNetwork =
        (e instanceof ApiError && e.status === 0) ||
        (!(e instanceof ApiError) && e instanceof TypeError);

      if (isNetwork) {
        await enqueue({
          url: path,
          method: "POST",
          body: meta.queuedBody ?? body,
          idempotencyKey: meta.idempotencyKey,
          jobId: meta.jobId,
          jobNumber: meta.jobNumber,
          actionType: meta.actionType,
        });
        return new Response(JSON.stringify({ queued: true }), {
          status: 202,
          headers: { "Content-Type": "application/json" },
        });
      }
      throw e;
    }
  }

  async function replayQueue(): Promise<void> {
    if (replayInProgress) return;
    replayInProgress = true;
    try {
      const db = await getDb();
      if (!db) return;

      // 1. Reclaim dead syncing rows from crashed/dead tabs (> 5 min ago).
      // Stale-only — never steal a fresh claim held by another live tab.
      await resetSyncing();

      // 2. Strict FIFO walk: claim one pending row at a time within readwrite tx.
      while (true) {
        // Single readwrite transaction to claim the next action.
        const claimTx = db.transaction(STORE, "readwrite");
        const all = await claimTx.store.getAll();
        // FIX 1: `failed` rows are TERMINAL (already surfaced in the UI banner)
        // — EXCLUDE them from the candidate set so a stale failed head doesn't
        // starve every later row forever. Stop-at-first-blocker only applies
        // to actionable states (pending / syncing / cooldown-skip).
        const candidates = all
          .filter((a) => a.status !== "done" && a.status !== "failed")
          .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));

        if (candidates.length === 0) {
          await claimTx.done;
          break;
        }

        const first = candidates[0];

        // If the first row is held by another live tab (status=syncing with a
        // fresh claim), stop the loop — that tab will finish it.
        if (first.status === "syncing") {
          await claimTx.done;
          break;
        }

        // If the first row is in cooldown, strict FIFO means we stop the loop.
        const now = Date.now();
        if (first.retryAt !== undefined && first.retryAt > now) {
          await claimTx.done;
          break;
        }

        // Claim the row.
        first.status = "syncing";
        first.claimedAt = Date.now();
        await claimTx.store.put(first);
        await claimTx.done;

        const nextAction = first;
        const id = nextAction.id as number;

        // 3. Process the claimed action.
        let outcome: ReplayOutcome;
        try {
          const res = await request(nextAction.url, {
            method: nextAction.method,
            headers: { "Idempotency-Key": nextAction.idempotencyKey },
            body: JSON.stringify(nextAction.body),
          });

          const body = res.ok ? {} : await parseErrorBody(res);
          outcome = classifyReplayOutcome(
            nextAction.actionType,
            res.status,
            body,
          );
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) {
            outcome = { kind: "pending", error: "Sign-in required" };
          } else if (e instanceof ApiError && e.status === 0) {
            outcome = { kind: "break" };
          } else if (e instanceof TypeError) {
            outcome = { kind: "break" };
          } else {
            const status = e instanceof ApiError ? e.status : 0;
            const body =
              e instanceof ApiError
                ? (e.body as { error?: string; reason?: string; retryAt?: string })
                : {};
            outcome =
              status > 0
                ? classifyReplayOutcome(nextAction.actionType, status, body)
                : { kind: "break" };
          }
        }

        // 4. Apply outcome. Strict FIFO: stop on anything other than "done".
        if (outcome.kind === "done") {
          await mark(id, {
            status: "done",
            syncedAt: Date.now(),
            error: undefined,
            claimedAt: undefined,
          });
          continue;
        }

        if (outcome.kind === "break") {
          await mark(id, {
            status: "pending",
            error: undefined,
            claimedAt: undefined,
          });
          break;
        }

        if (outcome.kind === "pending") {
          await mark(id, {
            status: "pending",
            ...(outcome.retryAt !== undefined ? { retryAt: outcome.retryAt } : {}),
            error: outcome.error,
            claimedAt: undefined,
          });
          break;
        }

        if (outcome.kind === "failed") {
          await mark(id, {
            status: "failed",
            error: outcome.error,
            claimedAt: undefined,
          });
          break;
        }

        if (outcome.kind === "verify-finish") {
          const landed = await verifyFinishLanded(nextAction.jobId, request);
          if (landed === "landed") {
            await mark(id, {
              status: "done",
              syncedAt: Date.now(),
              error: undefined,
              claimedAt: undefined,
            });
            continue;
          } else if (landed === "unknown") {
            await mark(id, {
              status: "pending",
              error: undefined,
              claimedAt: undefined,
            });
            break;
          } else {
            await mark(id, {
              status: "failed",
              error: "Could not finish — job is no longer in progress.",
              claimedAt: undefined,
            });
            break;
          }
        }

        if (outcome.kind === "verify-start") {
          const landed = await verifyStartLanded(nextAction.jobId, request);
          if (landed.kind === "landed") {
            await mark(id, {
              status: "done",
              syncedAt: Date.now(),
              error: undefined,
              claimedAt: undefined,
            });
            continue;
          } else if (landed.kind === "unknown") {
            await mark(id, {
              status: "pending",
              error: undefined,
              claimedAt: undefined,
            });
            break;
          } else {
            // CANCELLED / REVIEWED-without-IN_PROGRESS / other non-NEW-but-
            // not-landed states: the driver cannot start this job anymore.
            // Distinguish CANCELLED from "still NEW" in the error message
            // (the old message said "still NEW" even for CANCELLED jobs).
            const state = landed.state;
            const errorMsg =
              state === "NEW"
                ? "Could not start — job is still NEW."
                : `Job can no longer be started (state: ${state}).`;
            await mark(id, {
              status: "failed",
              error: errorMsg,
              claimedAt: undefined,
            });
            break;
          }
        }
      }

      notifyQueueChange();
    } finally {
      replayInProgress = false;
    }
  }

  /** Persist a status change on a single action (keeps id + key fields intact). */
  async function mark(
    id: number,
    patch: Partial<QueuedAction> & { status: QueuedActionStatus },
  ): Promise<void> {
    const db = await getDb();
    if (!db) return;
    const tx = db.transaction(STORE, "readwrite");
    const current = await tx.store.get(id);
    if (!current) {
      await tx.done;
      return;
    }
    const retryAt =
      patch.status === "pending" && patch.retryAt !== undefined
        ? patch.retryAt
        : patch.status === "pending"
          ? current.retryAt
          : undefined;

    await tx.store.put({ ...current, ...patch, retryAt });
    await tx.done;
  }

  async function getQueueStatus(): Promise<QueueStatus> {
    const db = await getDb();
    if (!db) {
      return {
        pending: 0,
        failed: 0,
        done: 0,
        unavailable: true,
        pendingItems: [],
        failedItems: [],
      };
    }
    const all = await db.getAll(STORE);
    const byStatus = (s: QueuedActionStatus) =>
      all.filter((a) => a.status === s);
    return {
      pending: byStatus("pending").length + byStatus("syncing").length,
      failed: byStatus("failed").length,
      done: byStatus("done").length,
      pendingItems: all
        .filter((a) => a.status === "pending" || a.status === "syncing")
        .map((a) => ({
          jobNumber: a.jobNumber,
          actionType: a.actionType,
          error: a.error,
        })),
      failedItems: byStatus("failed").map((a) => ({
        jobNumber: a.jobNumber,
        actionType: a.actionType,
        error: a.error ?? "Replay failed",
      })),
    };
  }

  /**
   * Wire the global replay triggers: online, visibilitychange→visible, and a
   * 500ms debounce so online+focus don't double-run. Returns an unsubscribe
   * function.
   *
   * FIX 2: `forceReset: true` reclaims ALL syncing rows (developer / wipe
   * path). The default is stale-only — fresh claims held by another live
   * tab are NEVER stolen. Crashed tabs recover via the 5-min expiry.
   */
  function init(opts: { forceReset?: boolean } = {}): () => void {
    void resetSyncing({ forceAll: opts.forceReset ?? false });

    const debounced = () => {
      if (replayDebounce !== null) clearTimeout(replayDebounce);
      replayDebounce = setTimeout(() => {
        replayDebounce = null;
        void replayQueue();
      }, 500);
    };

    const onOnline = () => debounced();
    const onVisibility = () => {
      if (document.visibilityState === "visible") debounced();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
      if (replayDebounce !== null) clearTimeout(replayDebounce);
    };
  }

  return {
    _setDbForTesting,
    onQueueChange,
    resetSyncing,
    enqueue,
    queuedApiFetch,
    replayQueue,
    getQueueStatus,
    init,
  };
}

// ── Default singleton ────────────────────────────────────────────────────────
// Existing call sites (`enqueue`, `queuedApiFetch`, `initOfflineQueue`, …)
// keep their original imports — the singleton preserves that surface.

const defaultQueue: OfflineQueue = createQueue();

/** Testing helper to inject a mock DB promise or simulate open failure. */
export function _setDbForTesting(
  override: Promise<IDBPDatabase<OfflineQueueDB> | null> | null,
): void {
  defaultQueue._setDbForTesting(override);
}

/** Subscribe to queue changes. Returns an unsubscribe function. */
export function onQueueChange(callback: () => void): () => void {
  return defaultQueue.onQueueChange(callback);
}

/**
 * Reclaim rows held in "syncing" state. Stale-only by default — pass
 * `{ forceAll: true }` to reclaim ALL syncing rows (developer / wipe path).
 */
export function resetSyncing(
  forceAll: boolean | { forceAll?: boolean } = false,
): Promise<void> {
  if (typeof forceAll === "boolean") {
    return defaultQueue.resetSyncing({ forceAll });
  }
  return defaultQueue.resetSyncing(forceAll);
}

/** Store an action for later replay. */
export function enqueue(
  action: Omit<
    QueuedAction,
    "id" | "status" | "queuedAt" | "syncedAt" | "error" | "claimedAt"
  > & { retryAt?: number },
): Promise<void> {
  return defaultQueue.enqueue(action);
}

/** Wrap a state-transition POST with offline awareness. */
export function queuedApiFetch(
  path: string,
  body: unknown,
  meta: {
    idempotencyKey: string;
    jobId: string;
    jobNumber: string;
    actionType: QueuedActionType;
    queuedBody?: unknown;
  },
): Promise<Response> {
  return defaultQueue.queuedApiFetch(path, body, meta);
}

/** Re-run pending actions in strict FIFO order. */
export function replayQueue(): Promise<void> {
  return defaultQueue.replayQueue();
}

/** Read the current queue status for UI banners. */
export function getQueueStatus(): Promise<QueueStatus> {
  return defaultQueue.getQueueStatus();
}

/**
 * Wire the global replay triggers. Default = stale-only reclaim — does NOT
 * steal fresh claims from other live tabs. (Send-back #2 FIX 2.)
 */
export function initOfflineQueue(): void {
  defaultQueue.init();
}

// Re-export SessionUser for any caller that imports through the queue module
// (kept for compatibility with the old surface; the actual import lives in api.ts).
export type { SessionUser };
